import { Topic } from '../../../grades/components/GradeLessonPage';

export const preAlgebraTopic: Topic = {
  id: 'pre-algebra',
  title: 'Pre-Algebra',
  icon: 'ri-calculator-line',
  color: 'violet',
  skills: [
    {
      id: 'whole-numbers',
      title: 'Whole Numbers & Operations',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Whole numbers are the counting numbers starting from zero (0, 1, 2, 3, ...). Understanding operations with whole numbers—addition, subtraction, multiplication, and division—is fundamental to all mathematics. The ACT tests your ability to perform multi-step calculations, understand order of operations (PEMDAS), and apply these operations to real-world problems. Mastery includes working with large numbers, estimating results, and recognizing patterns in number sequences.',
      questions: [
        {
          question: 'Calculate: 456 + 789 - 234',
          solution: 'Step 1: Add 456 + 789 = 1,245\nStep 2: Subtract 234 from 1,245\n1,245 - 234 = 1,011',
          answer: '1,011'
        },
        {
          question: 'What is 48 × 37?',
          solution: 'Step 1: Break down using distributive property\n48 × 37 = 48 × (30 + 7)\nStep 2: 48 × 30 = 1,440\nStep 3: 48 × 7 = 336\nStep 4: Add results: 1,440 + 336 = 1,776',
          answer: '1,776'
        },
        {
          question: 'Divide 2,856 by 12',
          solution: 'Step 1: Set up division: 2,856 ÷ 12\nStep 2: 12 goes into 28 twice (2 × 12 = 24), remainder 4\nStep 3: Bring down 5 to make 45. 12 goes into 45 three times (3 × 12 = 36), remainder 9\nStep 4: Bring down 6 to make 96. 12 goes into 96 eight times (8 × 12 = 96), remainder 0\nAnswer: 238',
          answer: '238'
        },
        {
          question: 'Evaluate: 5 + 3 × 8 - 4',
          solution: 'Step 1: Follow order of operations (PEMDAS)\nStep 2: Multiply first: 3 × 8 = 24\nStep 3: Now we have: 5 + 24 - 4\nStep 4: Add and subtract from left to right: 5 + 24 = 29\nStep 5: 29 - 4 = 25',
          answer: '25'
        },
        {
          question: 'What is the value of 15² - 8²?',
          solution: 'Step 1: Calculate 15² = 15 × 15 = 225\nStep 2: Calculate 8² = 8 × 8 = 64\nStep 3: Subtract: 225 - 64 = 161',
          answer: '161'
        },
        {
          question: 'Find the sum of all whole numbers from 1 to 10',
          solution: 'Step 1: List the numbers: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10\nStep 2: Add them: 1 + 2 + 3 + 4 + 5 + 6 + 7 + 8 + 9 + 10\nStep 3: Group for easier addition: (1+10) + (2+9) + (3+8) + (4+7) + (5+6)\nStep 4: Each pair sums to 11, and there are 5 pairs\nStep 5: 11 × 5 = 55',
          answer: '55'
        },
        {
          question: 'Calculate: (24 + 16) ÷ 8 × 3',
          solution: 'Step 1: Solve parentheses first: 24 + 16 = 40\nStep 2: Now we have: 40 ÷ 8 × 3\nStep 3: Division and multiplication from left to right: 40 ÷ 8 = 5\nStep 4: 5 × 3 = 15',
          answer: '15'
        },
        {
          question: 'What is 1,000 - 347 + 89?',
          solution: 'Step 1: Subtract first: 1,000 - 347 = 653\nStep 2: Add: 653 + 89 = 742',
          answer: '742'
        },
        {
          question: 'If 36 × n = 1,296, what is n?',
          solution: 'Step 1: Set up division: n = 1,296 ÷ 36\nStep 2: Divide: 1,296 ÷ 36 = 36\nStep 3: Check: 36 × 36 = 1,296 ✓',
          answer: '36'
        },
        {
          question: 'Evaluate: 100 - (25 + 15) × 2',
          solution: 'Step 1: Solve parentheses: 25 + 15 = 40\nStep 2: Multiply: 40 × 2 = 80\nStep 3: Subtract: 100 - 80 = 20',
          answer: '20'
        }
      ]
    },
    {
      id: 'fractions',
      title: 'Fractions',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Fractions represent parts of a whole and are written as a numerator over a denominator (a/b). The ACT tests your ability to add, subtract, multiply, and divide fractions, convert between mixed numbers and improper fractions, simplify fractions, and compare fractions. Understanding equivalent fractions and finding common denominators is essential. Real-world applications include recipes, measurements, and probability.',
      questions: [
        {
          question: 'Add: 2/5 + 3/10',
          solution: 'Step 1: Find common denominator (LCD = 10)\nStep 2: Convert 2/5 to tenths: 2/5 = 4/10\nStep 3: Add: 4/10 + 3/10 = 7/10',
          answer: '7/10'
        },
        {
          question: 'Multiply: 3/4 × 2/3',
          solution: 'Step 1: Multiply numerators: 3 × 2 = 6\nStep 2: Multiply denominators: 4 × 3 = 12\nStep 3: Result: 6/12\nStep 4: Simplify by dividing both by 6: 6/12 = 1/2',
          answer: '1/2'
        },
        {
          question: 'Divide: 5/6 ÷ 2/3',
          solution: 'Step 1: Multiply by the reciprocal: 5/6 × 3/2\nStep 2: Multiply numerators: 5 × 3 = 15\nStep 3: Multiply denominators: 6 × 2 = 12\nStep 4: Result: 15/12\nStep 5: Simplify: 15/12 = 5/4 = 1 1/4',
          answer: '5/4 or 1 1/4'
        },
        {
          question: 'Subtract: 7/8 - 1/4',
          solution: 'Step 1: Find common denominator (LCD = 8)\nStep 2: Convert 1/4 to eighths: 1/4 = 2/8\nStep 3: Subtract: 7/8 - 2/8 = 5/8',
          answer: '5/8'
        },
        {
          question: 'Convert 3 2/5 to an improper fraction',
          solution: 'Step 1: Multiply whole number by denominator: 3 × 5 = 15\nStep 2: Add the numerator: 15 + 2 = 17\nStep 3: Place over original denominator: 17/5',
          answer: '17/5'
        },
        {
          question: 'Simplify: 24/36',
          solution: 'Step 1: Find GCF of 24 and 36\nStep 2: Factors of 24: 1, 2, 3, 4, 6, 8, 12, 24\nStep 3: Factors of 36: 1, 2, 3, 4, 6, 9, 12, 18, 36\nStep 4: GCF = 12\nStep 5: Divide both by 12: 24÷12 = 2, 36÷12 = 3\nStep 6: Simplified: 2/3',
          answer: '2/3'
        },
        {
          question: 'Calculate: 1/2 + 1/3 + 1/6',
          solution: 'Step 1: Find LCD of 2, 3, and 6 (LCD = 6)\nStep 2: Convert: 1/2 = 3/6, 1/3 = 2/6, 1/6 = 1/6\nStep 3: Add: 3/6 + 2/6 + 1/6 = 6/6 = 1',
          answer: '1'
        },
        {
          question: 'What is 2/3 of 45?',
          solution: 'Step 1: "Of" means multiply: 2/3 × 45\nStep 2: Write 45 as 45/1: 2/3 × 45/1\nStep 3: Multiply: (2 × 45)/(3 × 1) = 90/3\nStep 4: Divide: 90 ÷ 3 = 30',
          answer: '30'
        },
        {
          question: 'Compare: Which is larger, 5/8 or 3/5?',
          solution: 'Step 1: Find common denominator (LCD = 40)\nStep 2: Convert 5/8: 5/8 = 25/40\nStep 3: Convert 3/5: 3/5 = 24/40\nStep 4: Compare: 25/40 > 24/40\nStep 5: Therefore, 5/8 > 3/5',
          answer: '5/8 is larger'
        },
        {
          question: 'Evaluate: (3/4 + 1/2) × 2/3',
          solution: 'Step 1: Add fractions in parentheses (LCD = 4)\nStep 2: 3/4 + 2/4 = 5/4\nStep 3: Multiply: 5/4 × 2/3\nStep 4: (5 × 2)/(4 × 3) = 10/12\nStep 5: Simplify: 10/12 = 5/6',
          answer: '5/6'
        }
      ]
    },
    {
      id: 'decimals',
      title: 'Decimals',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Decimals are another way to represent fractions and parts of a whole, using place value (tenths, hundredths, thousandths, etc.). The ACT requires proficiency in adding, subtracting, multiplying, and dividing decimals, as well as converting between decimals, fractions, and percents. Understanding decimal place value and rounding is crucial for estimation and real-world applications like money, measurements, and scientific notation.',
      questions: [
        {
          question: 'Add: 3.45 + 12.8 + 0.675',
          solution: 'Step 1: Align decimal points:\n  3.450\n 12.800\n+ 0.675\nStep 2: Add column by column from right to left\nStep 3: 0 + 0 + 5 = 5\nStep 4: 5 + 0 + 7 = 12 (write 2, carry 1)\nStep 5: 4 + 8 + 6 + 1 = 19 (write 9, carry 1)\nStep 6: 3 + 2 + 0 + 1 = 6\nStep 7: 0 + 1 + 0 = 1\nAnswer: 16.925',
          answer: '16.925'
        },
        {
          question: 'Multiply: 4.5 × 0.8',
          solution: 'Step 1: Ignore decimals and multiply: 45 × 8 = 360\nStep 2: Count decimal places: 4.5 (1 place) + 0.8 (1 place) = 2 places\nStep 3: Place decimal: 3.60 = 3.6',
          answer: '3.6'
        },
        {
          question: 'Divide: 15.6 ÷ 0.4',
          solution: 'Step 1: Move decimal in divisor to make it whole: 0.4 → 4\nStep 2: Move decimal same places in dividend: 15.6 → 156\nStep 3: Divide: 156 ÷ 4 = 39',
          answer: '39'
        },
        {
          question: 'Subtract: 20 - 7.35',
          solution: 'Step 1: Write with aligned decimals:\n 20.00\n- 7.35\nStep 2: Subtract column by column\nStep 3: Borrow as needed: 19.100 - 7.35\nStep 4: Result: 12.65',
          answer: '12.65'
        },
        {
          question: 'Convert 3/8 to a decimal',
          solution: 'Step 1: Divide numerator by denominator: 3 ÷ 8\nStep 2: 8 goes into 3.000 zero times\nStep 3: 8 goes into 30 three times (24), remainder 6\nStep 4: 8 goes into 60 seven times (56), remainder 4\nStep 5: 8 goes into 40 five times (40), remainder 0\nAnswer: 0.375',
          answer: '0.375'
        },
        {
          question: 'Round 45.6783 to the nearest hundredth',
          solution: 'Step 1: Identify hundredths place: 45.67[8]3\nStep 2: Look at next digit (thousandths): 8\nStep 3: Since 8 ≥ 5, round up\nStep 4: 45.67 becomes 45.68',
          answer: '45.68'
        },
        {
          question: 'Calculate: 0.25 × 0.25',
          solution: 'Step 1: Multiply without decimals: 25 × 25 = 625\nStep 2: Count decimal places: 0.25 (2) + 0.25 (2) = 4 places\nStep 3: Place decimal: 0.0625',
          answer: '0.0625'
        },
        {
          question: 'What is 12.5% of 80 expressed as a decimal calculation?',
          solution: 'Step 1: Convert percent to decimal: 12.5% = 0.125\nStep 2: Multiply: 0.125 × 80\nStep 3: 125 × 80 = 10,000\nStep 4: Place decimal (3 places): 10.000 = 10',
          answer: '10'
        },
        {
          question: 'Divide: 7.2 ÷ 0.09',
          solution: 'Step 1: Move decimal 2 places right in both: 720 ÷ 9\nStep 2: Divide: 720 ÷ 9 = 80',
          answer: '80'
        },
        {
          question: 'Evaluate: (3.6 + 2.4) × 1.5',
          solution: 'Step 1: Add in parentheses: 3.6 + 2.4 = 6.0\nStep 2: Multiply: 6.0 × 1.5\nStep 3: 60 × 15 = 900\nStep 4: Place decimal (1 + 1 = 2 places): 9.00 = 9',
          answer: '9'
        }
      ]
    },
    {
      id: 'percents',
      title: 'Percents',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Percent means "per hundred" and represents a ratio out of 100. The ACT tests your ability to convert between percents, decimals, and fractions; calculate percent of a number; find percent increase and decrease; and solve percent word problems. Understanding percents is essential for real-world applications including discounts, taxes, tips, interest rates, and statistical data interpretation.',
      questions: [
        {
          question: 'What is 35% of 200?',
          solution: 'Step 1: Convert percent to decimal: 35% = 0.35\nStep 2: Multiply: 0.35 × 200\nStep 3: 35 × 2 = 70',
          answer: '70'
        },
        {
          question: 'Convert 0.625 to a percent',
          solution: 'Step 1: Multiply decimal by 100: 0.625 × 100 = 62.5\nStep 2: Add percent symbol: 62.5%',
          answer: '62.5%'
        },
        {
          question: 'What percent of 80 is 20?',
          solution: 'Step 1: Set up equation: (20/80) × 100 = ?\nStep 2: Divide: 20 ÷ 80 = 0.25\nStep 3: Convert to percent: 0.25 × 100 = 25%',
          answer: '25%'
        },
        {
          question: 'A shirt costs $40. With a 25% discount, what is the sale price?',
          solution: 'Step 1: Calculate discount: 25% of $40 = 0.25 × 40 = $10\nStep 2: Subtract from original: $40 - $10 = $30\nAlternative: Sale price = 75% of original = 0.75 × 40 = $30',
          answer: '$30'
        },
        {
          question: 'A population increased from 500 to 650. What is the percent increase?',
          solution: 'Step 1: Find the increase: 650 - 500 = 150\nStep 2: Divide by original: 150 ÷ 500 = 0.30\nStep 3: Convert to percent: 0.30 × 100 = 30%',
          answer: '30%'
        },
        {
          question: 'Convert 7/8 to a percent',
          solution: 'Step 1: Divide: 7 ÷ 8 = 0.875\nStep 2: Multiply by 100: 0.875 × 100 = 87.5\nStep 3: Add percent symbol: 87.5%',
          answer: '87.5%'
        },
        {
          question: 'If 60% of students passed, and 24 students passed, how many students total?',
          solution: 'Step 1: Set up equation: 0.60 × total = 24\nStep 2: Divide both sides by 0.60: total = 24 ÷ 0.60\nStep 3: Calculate: 24 ÷ 0.60 = 40 students',
          answer: '40 students'
        },
        {
          question: 'A price decreased from $120 to $90. What is the percent decrease?',
          solution: 'Step 1: Find decrease: $120 - $90 = $30\nStep 2: Divide by original: 30 ÷ 120 = 0.25\nStep 3: Convert to percent: 0.25 × 100 = 25%',
          answer: '25%'
        },
        {
          question: 'What is 150% of 60?',
          solution: 'Step 1: Convert to decimal: 150% = 1.50\nStep 2: Multiply: 1.50 × 60 = 90',
          answer: '90'
        },
        {
          question: 'A restaurant bill is $85. If you want to leave an 18% tip, how much is the tip?',
          solution: 'Step 1: Convert percent to decimal: 18% = 0.18\nStep 2: Multiply: 0.18 × $85\nStep 3: 18 × 85 = 1,530\nStep 4: Place decimal: $15.30',
          answer: '$15.30'
        }
      ]
    },
    {
      id: 'ratios-proportions',
      title: 'Ratios & Proportions',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'A ratio compares two quantities and can be written as a:b or a/b. A proportion states that two ratios are equal (a/b = c/d). The ACT tests your ability to set up and solve proportions, work with rates and unit rates, scale drawings, and similar figures. Cross-multiplication is a key technique for solving proportions. Applications include recipe conversions, map scales, speed/distance/time problems, and unit conversions.',
      questions: [
        {
          question: 'Solve the proportion: 3/5 = x/20',
          solution: 'Step 1: Cross-multiply: 3 × 20 = 5 × x\nStep 2: 60 = 5x\nStep 3: Divide both sides by 5: x = 60 ÷ 5\nStep 4: x = 12',
          answer: 'x = 12'
        },
        {
          question: 'A recipe calls for 2 cups of flour for 3 dozen cookies. How much flour for 9 dozen?',
          solution: 'Step 1: Set up proportion: 2/3 = x/9\nStep 2: Cross-multiply: 2 × 9 = 3 × x\nStep 3: 18 = 3x\nStep 4: Divide: x = 18 ÷ 3 = 6 cups',
          answer: '6 cups'
        },
        {
          question: 'If a car travels 240 miles in 4 hours, what is the unit rate in miles per hour?',
          solution: 'Step 1: Divide distance by time: 240 miles ÷ 4 hours\nStep 2: 240 ÷ 4 = 60\nStep 3: Unit rate: 60 miles per hour',
          answer: '60 mph'
        },
        {
          question: 'The ratio of boys to girls is 3:4. If there are 21 boys, how many girls?',
          solution: 'Step 1: Set up proportion: 3/4 = 21/x\nStep 2: Cross-multiply: 3x = 4 × 21\nStep 3: 3x = 84\nStep 4: Divide: x = 84 ÷ 3 = 28 girls',
          answer: '28 girls'
        },
        {
          question: 'On a map, 1 inch represents 25 miles. How many miles does 3.5 inches represent?',
          solution: 'Step 1: Set up proportion: 1/25 = 3.5/x\nStep 2: Cross-multiply: 1 × x = 25 × 3.5\nStep 3: x = 87.5 miles',
          answer: '87.5 miles'
        },
        {
          question: 'Simplify the ratio 24:36',
          solution: 'Step 1: Find GCF of 24 and 36\nStep 2: GCF = 12\nStep 3: Divide both by 12: 24÷12 = 2, 36÷12 = 3\nStep 4: Simplified ratio: 2:3',
          answer: '2:3'
        },
        {
          question: 'If 5 pounds of apples cost $8, how much do 12 pounds cost?',
          solution: 'Step 1: Set up proportion: 5/8 = 12/x\nStep 2: Cross-multiply: 5x = 8 × 12\nStep 3: 5x = 96\nStep 4: Divide: x = 96 ÷ 5 = $19.20',
          answer: '$19.20'
        },
        {
          question: 'A triangle has sides in the ratio 3:4:5. If the shortest side is 15 cm, find the longest side.',
          solution: 'Step 1: Shortest side corresponds to 3 in ratio\nStep 2: Set up proportion: 3/15 = 5/x\nStep 3: Cross-multiply: 3x = 5 × 15\nStep 4: 3x = 75\nStep 5: x = 75 ÷ 3 = 25 cm',
          answer: '25 cm'
        },
        {
          question: 'Convert 60 miles per hour to feet per second (1 mile = 5,280 feet)',
          solution: 'Step 1: Convert miles to feet: 60 × 5,280 = 316,800 feet per hour\nStep 2: Convert hours to seconds: 1 hour = 3,600 seconds\nStep 3: Divide: 316,800 ÷ 3,600 = 88 feet per second',
          answer: '88 feet per second'
        },
        {
          question: 'The ratio of length to width of a rectangle is 5:3. If the width is 24 inches, what is the length?',
          solution: 'Step 1: Set up proportion: 5/3 = x/24\nStep 2: Cross-multiply: 3x = 5 × 24\nStep 3: 3x = 120\nStep 4: Divide: x = 120 ÷ 3 = 40 inches',
          answer: '40 inches'
        }
      ]
    },
    {
      id: 'integers',
      title: 'Integers & Operations',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Integers include all whole numbers and their negatives (..., -3, -2, -1, 0, 1, 2, 3, ...). The ACT tests operations with integers including addition, subtraction, multiplication, and division with positive and negative numbers. Key concepts include understanding absolute value, number line representation, and the rules for combining signs. Applications include temperature changes, elevation, financial gains/losses, and coordinate geometry.',
      questions: [
        {
          question: 'Calculate: -15 + 23',
          solution: 'Step 1: Different signs, so subtract absolute values\nStep 2: |23| - |-15| = 23 - 15 = 8\nStep 3: Take sign of larger absolute value (positive)\nAnswer: 8',
          answer: '8'
        },
        {
          question: 'Multiply: (-6) × (-8)',
          solution: 'Step 1: Multiply absolute values: 6 × 8 = 48\nStep 2: Apply sign rule: negative × negative = positive\nAnswer: 48',
          answer: '48'
        },
        {
          question: 'Subtract: -12 - (-7)',
          solution: 'Step 1: Subtracting negative is same as adding positive\nStep 2: -12 - (-7) = -12 + 7\nStep 3: Different signs, subtract: 12 - 7 = 5\nStep 4: Take sign of larger absolute value: -5',
          answer: '-5'
        },
        {
          question: 'Divide: -56 ÷ 8',
          solution: 'Step 1: Divide absolute values: 56 ÷ 8 = 7\nStep 2: Apply sign rule: negative ÷ positive = negative\nAnswer: -7',
          answer: '-7'
        },
        {
          question: 'What is the absolute value of -18?',
          solution: 'Step 1: Absolute value is distance from zero\nStep 2: |-18| = 18\nStep 3: Always positive (or zero)',
          answer: '18'
        },
        {
          question: 'Calculate: (-4) × 5 × (-2)',
          solution: 'Step 1: Multiply first two: (-4) × 5 = -20\nStep 2: Multiply result by third: (-20) × (-2)\nStep 3: Negative × negative = positive\nStep 4: 20 × 2 = 40',
          answer: '40'
        },
        {
          question: 'Evaluate: -8 + (-12) - 5',
          solution: 'Step 1: Add first two (same signs): -8 + (-12) = -20\nStep 2: Subtract 5: -20 - 5 = -25',
          answer: '-25'
        },
        {
          question: 'What is (-3)³?',
          solution: 'Step 1: Expand: (-3)³ = (-3) × (-3) × (-3)\nStep 2: First multiplication: (-3) × (-3) = 9\nStep 3: Second multiplication: 9 × (-3) = -27',
          answer: '-27'
        },
        {
          question: 'Solve: |x| = 9',
          solution: 'Step 1: Absolute value equation has two solutions\nStep 2: x = 9 or x = -9\nStep 3: Both have absolute value of 9',
          answer: 'x = 9 or x = -9'
        },
        {
          question: 'Calculate: 15 - 28 + (-6)',
          solution: 'Step 1: Subtract: 15 - 28 = -13\nStep 2: Add negative: -13 + (-6) = -19',
          answer: '-19'
        }
      ]
    },
    {
      id: 'factors-multiples',
      title: 'Factors & Multiples',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Factors are numbers that divide evenly into another number, while multiples are the products of a number and integers. The ACT tests your understanding of prime factorization, greatest common factor (GCF), least common multiple (LCM), prime and composite numbers, and divisibility rules. These concepts are fundamental for simplifying fractions, finding common denominators, and solving various number theory problems.',
      questions: [
        {
          question: 'List all factors of 36',
          solution: 'Step 1: Find pairs that multiply to 36\nStep 2: 1 × 36, 2 × 18, 3 × 12, 4 × 9, 6 × 6\nStep 3: List all unique factors in order\nFactors: 1, 2, 3, 4, 6, 9, 12, 18, 36',
          answer: '1, 2, 3, 4, 6, 9, 12, 18, 36'
        },
        {
          question: 'Find the GCF of 48 and 72',
          solution: 'Step 1: List factors of 48: 1, 2, 3, 4, 6, 8, 12, 16, 24, 48\nStep 2: List factors of 72: 1, 2, 3, 4, 6, 8, 9, 12, 18, 24, 36, 72\nStep 3: Identify common factors: 1, 2, 3, 4, 6, 8, 12, 24\nStep 4: Greatest common factor: 24',
          answer: '24'
        },
        {
          question: 'Find the LCM of 12 and 15',
          solution: 'Step 1: List multiples of 12: 12, 24, 36, 48, 60, 72...\nStep 2: List multiples of 15: 15, 30, 45, 60, 75...\nStep 3: Find first common multiple: 60',
          answer: '60'
        },
        {
          question: 'What is the prime factorization of 84?',
          solution: 'Step 1: Divide by smallest prime: 84 ÷ 2 = 42\nStep 2: Continue: 42 ÷ 2 = 21\nStep 3: 21 ÷ 3 = 7\nStep 4: 7 is prime\nStep 5: Prime factorization: 2² × 3 × 7',
          answer: '2² × 3 × 7'
        },
        {
          question: 'Is 91 prime or composite?',
          solution: 'Step 1: Check divisibility by small primes\nStep 2: Not divisible by 2 (odd), 3 (9+1=10), 5 (doesn\'t end in 0 or 5)\nStep 3: Try 7: 91 ÷ 7 = 13 ✓\nStep 4: 91 = 7 × 13, so it\'s composite',
          answer: 'Composite (7 × 13)'
        },
        {
          question: 'What is the smallest number divisible by both 6 and 8?',
          solution: 'Step 1: This is asking for LCM of 6 and 8\nStep 2: Multiples of 6: 6, 12, 18, 24, 30...\nStep 3: Multiples of 8: 8, 16, 24, 32...\nStep 4: First common multiple: 24',
          answer: '24'
        },
        {
          question: 'How many prime numbers are there between 20 and 30?',
          solution: 'Step 1: List numbers: 21, 22, 23, 24, 25, 26, 27, 28, 29\nStep 2: Check each for prime:\n21 = 3×7 (composite)\n22 = 2×11 (composite)\n23 = prime ✓\n24 = 2³×3 (composite)\n25 = 5² (composite)\n26 = 2×13 (composite)\n27 = 3³ (composite)\n28 = 2²×7 (composite)\n29 = prime ✓\nStep 3: Count primes: 2 (23 and 29)',
          answer: '2 primes (23 and 29)'
        },
        {
          question: 'Find the GCF of 30, 45, and 60',
          solution: 'Step 1: Prime factorization:\n30 = 2 × 3 × 5\n45 = 3² × 5\n60 = 2² × 3 × 5\nStep 2: Find common prime factors: 3 and 5\nStep 3: Take lowest powers: 3¹ × 5¹\nStep 4: GCF = 15',
          answer: '15'
        },
        {
          question: 'What is the LCM of 4, 6, and 9?',
          solution: 'Step 1: Prime factorization:\n4 = 2²\n6 = 2 × 3\n9 = 3²\nStep 2: Take highest power of each prime: 2², 3²\nStep 3: Multiply: 4 × 9 = 36',
          answer: '36'
        },
        {
          question: 'A number has exactly 3 factors. What type of number is it?',
          solution: 'Step 1: Consider what numbers have exactly 3 factors\nStep 2: Example: 4 has factors 1, 2, 4 (3 factors)\nStep 3: 9 has factors 1, 3, 9 (3 factors)\nStep 4: Pattern: These are squares of prime numbers\nStep 5: p² has factors: 1, p, p² (exactly 3)',
          answer: 'Square of a prime number'
        }
      ]
    },
    {
      id: 'number-properties',
      title: 'Number Properties',
      standards: ['ACT Math: Pre-Algebra', 'Number & Quantity'],
      explanation: 'Number properties include the commutative, associative, and distributive properties, as well as properties of even and odd numbers, and the identity and inverse properties. The ACT tests your ability to recognize and apply these properties to simplify expressions and solve problems efficiently. Understanding these fundamental properties helps in mental math, algebraic manipulation, and recognizing patterns in number operations.',
      questions: [
        {
          question: 'Which property is shown: 5 × (3 + 7) = 5 × 3 + 5 × 7?',
          solution: 'Step 1: Identify the pattern\nStep 2: A number is multiplied by a sum\nStep 3: The multiplication is distributed to each term\nStep 4: This is the Distributive Property',
          answer: 'Distributive Property'
        },
        {
          question: 'Is the sum of two odd numbers even or odd?',
          solution: 'Step 1: Test with examples: 3 + 5 = 8 (even)\nStep 2: Try another: 7 + 9 = 16 (even)\nStep 3: General form: (2n+1) + (2m+1) = 2n + 2m + 2 = 2(n+m+1)\nStep 4: Result is always even',
          answer: 'Even'
        },
        {
          question: 'Simplify using properties: 25 × 17 × 4',
          solution: 'Step 1: Recognize 25 × 4 = 100\nStep 2: Use commutative property to rearrange: 25 × 4 × 17\nStep 3: Use associative property: (25 × 4) × 17\nStep 4: Calculate: 100 × 17 = 1,700',
          answer: '1,700'
        },
        {
          question: 'What is the multiplicative identity?',
          solution: 'Step 1: Identity element doesn\'t change the value\nStep 2: For multiplication: a × 1 = a\nStep 3: 1 is the multiplicative identity\nStep 4: Any number times 1 equals itself',
          answer: '1'
        },
        {
          question: 'Is the product of an even and an odd number even or odd?',
          solution: 'Step 1: Even number form: 2n\nStep 2: Odd number form: 2m + 1\nStep 3: Product: 2n × (2m + 1) = 2n(2m + 1)\nStep 4: Result has factor of 2, so it\'s even',
          answer: 'Even'
        },
        {
          question: 'Which property: (8 + 5) + 2 = 8 + (5 + 2)?',
          solution: 'Step 1: Observe the grouping changes\nStep 2: The order stays the same\nStep 3: Only parentheses (grouping) moved\nStep 4: This is the Associative Property of Addition',
          answer: 'Associative Property'
        },
        {
          question: 'Calculate mentally: 47 + 38 + 53',
          solution: 'Step 1: Use commutative property to rearrange\nStep 2: Group: (47 + 53) + 38\nStep 3: 47 + 53 = 100\nStep 4: 100 + 38 = 138',
          answer: '138'
        },
        {
          question: 'What is the additive inverse of -12?',
          solution: 'Step 1: Additive inverse means opposite\nStep 2: When added together, sum is 0\nStep 3: -12 + ? = 0\nStep 4: Additive inverse of -12 is 12',
          answer: '12'
        },
        {
          question: 'Is the product of three consecutive integers always divisible by 6?',
          solution: 'Step 1: Test: 2×3×4 = 24 ÷ 6 = 4 ✓\nStep 2: Test: 5×6×7 = 210 ÷ 6 = 35 ✓\nStep 3: Reasoning: Among 3 consecutive integers, at least one is even (divisible by 2) and at least one is divisible by 3\nStep 4: Product divisible by 2×3 = 6',
          answer: 'Yes, always'
        },
        {
          question: 'Simplify: 8(x + 5) - 3(x + 5)',
          solution: 'Step 1: Factor out common term (x + 5)\nStep 2: (x + 5)(8 - 3)\nStep 3: (x + 5)(5)\nStep 4: 5(x + 5) or 5x + 25',
          answer: '5(x + 5) or 5x + 25'
        }
      ]
    },
    {
      id: 'basic-probability',
      title: 'Basic Probability',
      standards: ['ACT Math: Pre-Algebra', 'Statistics & Probability'],
      explanation: 'Probability measures the likelihood of an event occurring, expressed as a number between 0 and 1 (or 0% to 100%). The basic probability formula is P(event) = (favorable outcomes)/(total possible outcomes). The ACT tests simple probability, compound probability, independent and dependent events, and probability with replacement and without replacement. Understanding probability is essential for making predictions and analyzing real-world situations involving chance.',
      questions: [
        {
          question: 'A bag contains 5 red, 3 blue, and 2 green marbles. What is the probability of drawing a red marble?',
          solution: 'Step 1: Count total marbles: 5 + 3 + 2 = 10\nStep 2: Count favorable outcomes (red): 5\nStep 3: P(red) = favorable/total = 5/10\nStep 4: Simplify: 5/10 = 1/2 or 0.5 or 50%',
          answer: '1/2 or 0.5 or 50%'
        },
        {
          question: 'What is the probability of rolling a number greater than 4 on a standard die?',
          solution: 'Step 1: Total outcomes on a die: 6 (1, 2, 3, 4, 5, 6)\nStep 2: Favorable outcomes (>4): 5 and 6 (2 outcomes)\nStep 3: P(>4) = 2/6\nStep 4: Simplify: 2/6 = 1/3',
          answer: '1/3'
        },
        {
          question: 'A coin is flipped twice. What is the probability of getting two heads?',
          solution: 'Step 1: List all outcomes: HH, HT, TH, TT (4 total)\nStep 2: Favorable outcome: HH (1 outcome)\nStep 3: P(HH) = 1/4\nAlternative: P(H) × P(H) = 1/2 × 1/2 = 1/4',
          answer: '1/4 or 0.25 or 25%'
        },
        {
          question: 'A deck has 52 cards. What is the probability of drawing a heart?',
          solution: 'Step 1: Total cards: 52\nStep 2: Number of hearts: 13\nStep 3: P(heart) = 13/52\nStep 4: Simplify: 13/52 = 1/4',
          answer: '1/4 or 0.25 or 25%'
        },
        {
          question: 'What is the probability of NOT rolling a 3 on a standard die?',
          solution: 'Step 1: P(rolling 3) = 1/6\nStep 2: P(NOT 3) = 1 - P(3)\nStep 3: P(NOT 3) = 1 - 1/6 = 5/6',
          answer: '5/6'
        },
        {
          question: 'Two dice are rolled. What is the probability that the sum is 7?',
          solution: 'Step 1: Total outcomes: 6 × 6 = 36\nStep 2: Ways to get sum of 7: (1,6), (2,5), (3,4), (4,3), (5,2), (6,1) = 6 ways\nStep 3: P(sum=7) = 6/36\nStep 4: Simplify: 6/36 = 1/6',
          answer: '1/6'
        },
        {
          question: 'A spinner has 8 equal sections: 3 red, 3 blue, 2 yellow. What is P(not yellow)?',
          solution: 'Step 1: Total sections: 8\nStep 2: Yellow sections: 2\nStep 3: Not yellow sections: 8 - 2 = 6\nStep 4: P(not yellow) = 6/8 = 3/4',
          answer: '3/4 or 0.75 or 75%'
        },
        {
          question: 'From 10 students (6 girls, 4 boys), one is chosen randomly. What is P(girl)?',
          solution: 'Step 1: Total students: 10\nStep 2: Number of girls: 6\nStep 3: P(girl) = 6/10\nStep 4: Simplify: 6/10 = 3/5',
          answer: '3/5 or 0.6 or 60%'
        },
        {
          question: 'A bag has 12 marbles: 5 red, 4 blue, 3 green. Two marbles are drawn with replacement. What is P(both red)?',
          solution: 'Step 1: P(first red) = 5/12\nStep 2: With replacement, P(second red) = 5/12\nStep 3: P(both red) = 5/12 × 5/12\nStep 4: = 25/144',
          answer: '25/144'
        },
        {
          question: 'What is the probability of drawing an ace or a king from a standard deck?',
          solution: 'Step 1: Number of aces: 4\nStep 2: Number of kings: 4\nStep 3: Total favorable: 4 + 4 = 8\nStep 4: P(ace or king) = 8/52 = 2/13',
          answer: '2/13'
        }
      ]
    },
    {
      id: 'data-interpretation',
      title: 'Data Interpretation',
      standards: ['ACT Math: Pre-Algebra', 'Statistics & Probability'],
      explanation: 'Data interpretation involves reading and analyzing information presented in tables, charts, graphs, and plots. The ACT tests your ability to extract information from bar graphs, line graphs, pie charts, scatter plots, and data tables; calculate measures of central tendency (mean, median, mode); and understand range and basic statistical concepts. This skill is essential for making data-driven decisions and understanding quantitative information in real-world contexts.',
      questions: [
        {
          question: 'Find the mean of: 12, 15, 18, 20, 25',
          solution: 'Step 1: Add all values: 12 + 15 + 18 + 20 + 25 = 90\nStep 2: Count values: 5\nStep 3: Divide sum by count: 90 ÷ 5 = 18\nMean = 18',
          answer: '18'
        },
        {
          question: 'Find the median of: 7, 3, 9, 12, 5, 8, 10',
          solution: 'Step 1: Arrange in order: 3, 5, 7, 8, 9, 10, 12\nStep 2: Count values: 7 (odd number)\nStep 3: Middle position: (7+1)/2 = 4th position\nStep 4: Median = 8',
          answer: '8'
        },
        {
          question: 'Find the mode of: 4, 7, 4, 9, 4, 6, 7, 4',
          solution: 'Step 1: Count frequency of each value:\n4 appears 4 times\n7 appears 2 times\n9 appears 1 time\n6 appears 1 time\nStep 2: Most frequent value: 4\nMode = 4',
          answer: '4'
        },
        {
          question: 'Find the range of: 23, 45, 12, 67, 34, 89, 28',
          solution: 'Step 1: Find maximum value: 89\nStep 2: Find minimum value: 12\nStep 3: Range = max - min\nStep 4: Range = 89 - 12 = 77',
          answer: '77'
        },
        {
          question: 'A pie chart shows: Math 30%, Science 25%, English 20%, History 15%, Art 10%. If 200 students were surveyed, how many chose Math?',
          solution: 'Step 1: Math represents 30% of students\nStep 2: Calculate: 30% of 200\nStep 3: 0.30 × 200 = 60 students',
          answer: '60 students'
        },
        {
          question: 'Test scores: 85, 90, 78, 92, 88. What score is needed on the 6th test for a mean of 87?',
          solution: 'Step 1: Sum of 6 tests for mean 87: 6 × 87 = 522\nStep 2: Current sum: 85 + 90 + 78 + 92 + 88 = 433\nStep 3: Needed score: 522 - 433 = 89',
          answer: '89'
        },
        {
          question: 'A bar graph shows sales: Mon=20, Tue=35, Wed=28, Thu=42, Fri=55. What is the average daily sales?',
          solution: 'Step 1: Add all sales: 20 + 35 + 28 + 42 + 55 = 180\nStep 2: Count days: 5\nStep 3: Average = 180 ÷ 5 = 36',
          answer: '36'
        },
        {
          question: 'Find the median of: 14, 22, 18, 30, 26, 20',
          solution: 'Step 1: Arrange in order: 14, 18, 20, 22, 26, 30\nStep 2: Count values: 6 (even number)\nStep 3: Middle two positions: 3rd and 4th (20 and 22)\nStep 4: Median = (20 + 22)/2 = 21',
          answer: '21'
        },
        {
          question: 'A line graph shows temperature: 8am=65°, 10am=72°, 12pm=78°, 2pm=75°, 4pm=70°. What was the temperature increase from 8am to 12pm?',
          solution: 'Step 1: Temperature at 8am: 65°\nStep 2: Temperature at 12pm: 78°\nStep 3: Increase = 78° - 65° = 13°',
          answer: '13°'
        },
        {
          question: 'Data set: 5, 8, 8, 10, 12, 15, 15, 15, 18. What is the mode and median?',
          solution: 'Step 1: Find mode (most frequent): 15 appears 3 times\nMode = 15\nStep 2: Data already ordered, count: 9 values\nStep 3: Middle position: (9+1)/2 = 5th position\nStep 4: Median = 12',
          answer: 'Mode = 15, Median = 12'
        }
      ]
    }
  ]
};